"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useTranslation = void 0;
exports.useTranslationMethods = useTranslationMethods;
exports.useTranslationValue = void 0;
require("core-js/modules/es.symbol.description.js");
var _react = require("react");
var _translate = require("./utils/translate");
var _methods = require("./utils/methods");
var _functions = require("../utils/functions");
var _reactRedux = require("react-redux");
var _translations = require("../entities/translations");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
/**
 * Hook returns translations for the active locale.
 * @category Hooks
 * @param {ITranslations} t, Translations
 * @param {string} l, Locale in case you need translations for a not active locale.
 * @param {string} defaultText Default text if translation is empty
 * @example
 * // Example of using translations in a function component.
 *
 * const translations = {
 *    example: {
 *      "en-US": "Example",
 *      "de-DE": "Beispiel"
 *    },
 *    hello: (name: string) => ({
 *      "en-US": `Hello ${name}`,
 *      "de-DE": `Hallo ${name}`
 *    }),
 *    news: [
 *      {
 *        en-US: "The spaghetti code monster ate our homework.",
 *        de-DE: "Das Spaghetti-Code-Monster aß unsere Hausaufgaben."
 *      }
 *    ]
 * }
 *
 * const YourComponent = () => {
 *    const translated = useLittera(translations);
 *
 *    return <>
 *        <h2>{translated.example} - {translated.hello("Mike")}</h2>
 *        <p>{translated.news[0]}</p>
 *    </>
 * }
 * @returns {ITranslated}
 */
const useTranslation = exports.useTranslation = function useTranslation(t) {
  var _ref, _context$preset, _context$locales;
  let l = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : undefined;
  let defaultText = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
  const context = (0, _reactRedux.useSelector)(_translations.selectAllByTranslations);
  if (context.locale === null && !l) throw new Error('Your app has to be wrapped with the LocalizationProvider to work properly. Alternatively you can provide the language argument.');
  const locale = (_ref = l !== null && l !== void 0 ? l : context.locale) !== null && _ref !== void 0 ? _ref : 'en-US';
  const preset = (_context$preset = context.preset) !== null && _context$preset !== void 0 ? _context$preset : {};
  const locales = (_context$locales = context.locales) !== null && _context$locales !== void 0 ? _context$locales : [locale];
  const translations = (0, _react.useMemo)(() => isTransFn(t) ? _objectSpread({}, t(preset)) : _objectSpread({}, t), [t, preset]);
  (0, _react.useEffect)(() => {
    (0, _methods.reportMissing)(translations, locales);
  }, [translations]);
  return (0, _react.useMemo)(() => (0, _translate.translate)(translations, locale, defaultText), [translations, locale, defaultText]);
};
function isTransFn(value) {
  return typeof value === 'function';
}

/**
 * Hook exposes an object with translation variables.
 * @category Hooks
 *
 * @example
 *
 * const YourComponent = () => {
 *    const example = {
 *      "en-US": "Example",
 *      "de-DE": "Beispiel"
 *    }
 *    const tV = useTranslationValue();
 *
 *    return <h2>{tV(example)}</h2>
 * }
 *
 * @returns {Function}
 *    first param - {object|string}
 *    second param - {object} with settings
 *       {defaultText = '', activeLanguage = 'en-US', defaultLanguage = 'de-DE'}
 */
const useTranslationValue = () => {
  const {
    locale
  } = (0, _reactRedux.useSelector)(_translations.selectAllByTranslations);
  return (0, _react.useCallback)(function (text) {
    let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return (0, _methods.getTextByLanguage)(text, _objectSpread({
      activeLanguage: locale
    }, params));
  }, [locale]);
};

/**
 * Hook exposes an object with global translation methods and variables.
 * @category Hooks
 * @example
 * // Example of accessing littera methods and variables in a function component.
 *
 * const YourComponent = () => {
 *    const { setLocale, locale } = useLittera(translations);
 *
 *    const handleClick = () => {
 *      setLocale("de-DE");
 *    }
 *
 *    return <h2 onClick={handleClick}>Current language: {locale}</h2>
 * }
 * @returns {Object} methods
 * @returns {string} methods.locale - active locale.
 * @returns {string[]} methods.locales - all locales.
 * @returns {Function} methods.setLocale - changes the active locale.
 * @returns {Function} methods.validateLocale - method validates the locale format using a pattern.
 * @returns {ITranslations} methods.preset - global preset.
 * @returns {Function} methods.translate - the core translation method.
 * @returns {Function} methods.translateSingle - the core single translation method.
 */
exports.useTranslationValue = useTranslationValue;
function useTranslationMethods() {
  const {
    locale,
    preset,
    pattern,
    locales
  } = (0, _reactRedux.useSelector)(_translations.selectAllByTranslations);
  const dispatch = (0, _reactRedux.useDispatch)();
  const _setLocale = (0, _react.useCallback)(locale => {
    if (!_validateLocale(locale)) throw new Error("Locale does not match the pattern.");
    (0, _functions.setToLocalStorage)('locale', locale);
    dispatch((0, _translations.setLocaleAction)(locale));
  }, [locale, dispatch]);
  const _validateLocale = (0, _react.useCallback)(_methods.validateLocale, [pattern]);
  return {
    locale,
    locales,
    setLocale: _setLocale,
    validateLocale: _validateLocale,
    preset,
    translate: _translate.translate,
    translateSingle: _translate.translateSingle
  };
}